import type { ButtonVariant } from '@mantine/core';
import { alpha, Button, Title, useMantineTheme } from '@mantine/core';
import clsx from 'clsx';
import React from 'react';
import remarkBreaks from 'remark-breaks';
import { ANNOUNCEMENT_IMAGE_WIDTH } from '~/components/Announcements/announcement-image';
import { EdgeMedia } from '~/components/EdgeMedia/EdgeMedia';
import { ImageGuard2 } from '~/components/ImageGuard/ImageGuard2';
import { MediaHash } from '~/components/ImageHash/ImageHash';
import { CustomMarkdown } from '~/components/Markdown/CustomMarkdown';
import { NextLink as Link } from '~/components/NextLink/NextLink';
import { TwCard } from '~/components/TwCard/TwCard';
import { useTrackImpression } from '~/components/TrackView/useTrackImpression';
import type { ImpressionTarget } from '~/components/TrackView/useTrackImpression';
import { externalLinkAnchorProps } from '~/components/ExternalLinkWarning/externalLinkAnchorProps';
import { useCurrentDomainHref } from '~/hooks/useCurrentDomainHref';
import { useInternalHosts } from '~/hooks/useInternalHosts';
import { isExternalHref } from '~/utils/external-link';

export type AnnouncementCardAction = {
  link: string;
  linkText: string;
  variant?: string;
  color?: string;
};

/**
 * The cover, in the two shapes the two authoring paths produce.
 *
 * `key` is a bare storage key with no `Image` row — how sitewide announcements have always
 * stored theirs, deliberately, so no row deletion can take the object with it. `image` is a
 * real `Image` row, which is what a creator upload becomes and what gets it NSFW-scanned.
 * A key cannot be guarded because there is nothing to rate; a row always is.
 */
export type AnnouncementCardCover =
  | { kind: 'key'; src: string }
  | {
      kind: 'image';
      nsfwLevel: number;
      id: number;
      url: string;
      type: 'image' | 'video' | 'audio';
      name?: string | null;
      hash?: string | null;
      width?: number | null;
      height?: number | null;
    };

type AnnouncementCardProps = {
  title?: string;
  content: string;
  color: string;
  cover?: AnnouncementCardCover | null;
  actions?: AnnouncementCardAction[];
  /**
   * Notified when one of `actions` is pressed, before the navigation. Analytics only, and
   * deliberately a callback rather than tracking done here: this component renders sitewide
   * and creator announcements alike, and only the creator ones are instrumented. Passing the
   * decision to the call site is what keeps that boolean out of a component whose contract is
   * layout.
   */
  onActionClick?: (action: AnnouncementCardAction, index: number) => void;
  /**
   * Entities this card should record an impression for once it has been half visible for a
   * continuous second. Omitted by the sitewide surface, which is not instrumented — the hook
   * disables itself on an empty list, so "creator only" is a property of who passes this.
   */
  impressions?: ImpressionTarget[];
  /**
   * A full-width bar across the top of the card, outside the cover/content row. Attribution
   * for a card Civitai did not write goes here rather than beside the title: the frame is
   * what a reader takes in before any of the card's own content.
   */
  topBar?: React.ReactNode;
  /** Rendered beside the title — moderator or author controls. */
  controls?: React.ReactNode;
  /** Absolutely positioned over the card, e.g. the dismiss button. */
  overlay?: React.ReactNode;
  footer?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * The one announcement card. Sitewide and creator announcements render through this so they
 * cannot drift apart visually — they had already drifted twice, on the cover breakpoint and
 * on how a missing title renders, before this existed.
 *
 * Everything that differs between the two lives in the call sites: dismissal and its store,
 * moderator or author controls, and the footer. This component decides layout and nothing
 * about policy.
 */
export function AnnouncementCard({
  title,
  content,
  color,
  cover,
  actions = [],
  onActionClick,
  impressions,
  topBar,
  controls,
  overlay,
  footer,
  className,
  style,
  ...props
}: AnnouncementCardProps) {
  const impressionRef = useTrackImpression<HTMLElement>(impressions);
  const theme = useMantineTheme();
  const internalHosts = useInternalHosts();
  const toCurrentDomain = useCurrentDomainHref();
  const borderColor = theme.colors[color]?.[4] ?? theme.colors.blue[4];

  const body = (
    <>
      {cover && (
        // Both halves of `size-40` are load-bearing. The height is what makes this square:
        // it is a definite cross size, so the row's `items-stretch` no longer applies, and
        // every child here is `absolute inset-0` and contributes no in-flow height, so
        // dropping it collapses the cover to nothing rather than falling back to the image.
        // Do not make the width responsive without pairing a height.
        //
        // Hidden only when the card itself is under 20rem — this is a container query, so
        // it tracks the card's width, not the viewport's.
        <div className="relative size-40 shrink-0 @max-xs:hidden">
          {cover.kind === 'key' ? (
            <EdgeMedia
              src={cover.src}
              // Shared with `announcement-media-check`: the explicit `optimized` keeps the banner
              // on ONE variant, so the monitor can name the URL users load. See announcement-image.ts.
              width={ANNOUNCEMENT_IMAGE_WIDTH}
              optimized
              alt="Announcement banner image"
              className="absolute inset-0 size-full object-cover"
            />
          ) : (
            <ImageGuard2 image={cover}>
              {(safe) =>
                !safe ? (
                  <MediaHash {...cover} style={{ width: '100%', height: '100%' }} />
                ) : (
                  <EdgeMedia
                    src={cover.url}
                    type={cover.type}
                    name={cover.name ?? cover.id.toString()}
                    alt={cover.name ?? undefined}
                    width={ANNOUNCEMENT_IMAGE_WIDTH}
                    className="absolute inset-0 size-full object-cover"
                  />
                )
              }
            </ImageGuard2>
          )}
        </div>
      )}

      <div
        className={clsx(
          'flex flex-1 flex-col justify-center gap-2 p-3',
          // Gated on the same container query that hides the cover: keyed on the DATA alone
          // this is a second border 1px inside the card's own on a narrow card.
          cover && 'border-l @max-xs:border-l-0'
        )}
        style={cover ? { borderColor } : undefined}
      >
        {(!!title || !!controls) && (
          <div className="flex justify-between gap-2">
            {!!title && <Title order={4}>{title}</Title>}
            {controls}
          </div>
        )}
        {/* `p` and `br` so the line breaks a creator typed survive: unwrapped paragraphs and
            soft breaks are both plain whitespace, which HTML collapses to a space. */}
        <CustomMarkdown
          allowedElements={['a', 'p', 'br']}
          unwrapDisallowed
          remarkPlugins={[remarkBreaks]}
          className="[&_p+p]:mt-2"
          warnOnExternalLinks
        >
          {content}
        </CustomMarkdown>
        {!!actions.length && (
          <div className="flex flex-wrap gap-2">
            {actions.map((action, index) => {
              const variant = (action.variant || 'outline') as ButtonVariant;
              const shared = {
                variant,
                color: action.color ?? color,
                children: action.linkText,
              };

              if (!isExternalHref(action.link, internalHosts))
                return (
                  <Button
                    key={index}
                    component={Link}
                    href={toCurrentDomain(action.link)}
                    onClick={() => onActionClick?.(action, index)}
                    {...shared}
                  />
                );

              return (
                <Button
                  key={index}
                  component="a"
                  {...externalLinkAnchorProps(action.link, () => onActionClick?.(action, index))}
                  {...shared}
                />
              );
            })}
          </div>
        )}
        {footer}
      </div>
    </>
  );

  if (!topBar)
    return (
      <TwCard
        ref={impressionRef}
        className={clsx('items-stretch border', className)}
        direction="row"
        style={{ borderColor, ...style }}
        {...props}
      >
        {overlay}
        {body}
      </TwCard>
    );

  return (
    <TwCard
      ref={impressionRef}
      className={clsx('border', className)}
      direction="col"
      style={{ borderColor, ...style }}
      {...props}
    >
      {overlay}
      {/* A wash of the border colour rather than the colour itself: the bar has to read as
          part of the frame without competing with the announcement's own content. */}
      <div
        className="w-full border-b px-3 py-2"
        style={{ backgroundColor: alpha(borderColor, 0.15), borderColor }}
      >
        {topBar}
      </div>
      <div className="flex flex-1 items-stretch">{body}</div>
    </TwCard>
  );
}
